import { useEffect, useState, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, IconButton, Box, Typography,
    Table, TableHead, TableBody, TableRow, TableCell,
    Select, MenuItem, FormControl, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { createAmendment } from '../../api/commissionAmendmentApi';
import { getAllAgents } from '../../api/userApi';
import handleAgentName from '../../utils/userUtils';

const COMM_TYPES = ['PSC', 'DOC', 'TOC', 'RefC'];

const poppins = "'Poppins', sans-serif";

export default function AmendCommissionDialog({ open, onClose, onSaved, salesId, commissionLines, finalPrice, originalPrice, discountByAstern, discountByAgent, coAgentName }) {
    const [lines, setLines] = useState([]);
    const [reason, setReason] = useState('');
    const [agents, setAgents] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const fetchAgents = useCallback(async () => {
        try {
            const res = await getAllAgents();
            setAgents(res.data);
        } catch (e) {
            console.error('Failed to fetch agents', e);
        }
    }, []);

    useEffect(() => {
        if (open) fetchAgents();
    }, [open, fetchAgents]);

    useEffect(() => {
        if (open && commissionLines) {
            setLines(commissionLines
                .filter(l => !['Total Commission', 'Webbey Commission'].includes(l.commPayType))
                .map(l => ({
                    paymentTo: l.paymentTo,
                    paymentToName: l.paymentToName,
                    commPayType: l.commPayType,
                    commPay: l.commPay,
                    rate: l.rate,
                    editMode: 'rate', // 'rate' = edit rate, auto-calc amount; 'amount' = edit amount, auto-calc rate
                }))
            );
            setReason('');
        }
    }, [open, commissionLines]);

    const commBase = (originalPrice || 0) - (discountByAstern || 0);
    const commBaseHalf = commBase / 2;
    const agentDisc = discountByAgent || 0;
    const isCoBroke = !!coAgentName;

    // Detect if a PSC line uses FinalPrice (in-house agent) based on original values
    const isInHousePsc = (origRate, origAmount) => {
        if (!origRate || !finalPrice) return false;
        const fpCalc = finalPrice * origRate / 100;
        return Math.abs(fpCalc - origAmount) < 1; // within RM 1 tolerance
    };

    const updateLine = (index, field, value) => {
        setLines(prev => {
            const updated = [...prev];
            const line = { ...updated[index] };
            // Store original rate/amount from when dialog opened (for detecting in-house)
            if (!line._origRate) { line._origRate = line.rate; line._origAmount = line.commPay; }

            if (field === 'editMode') {
                line.editMode = value;
                updated[index] = line;
                return updated;
            }

            if (field === 'rate') {
                line.editMode = 'rate';
                const rate = parseFloat(value) || 0;
                line.rate = rate;

                if (line.commPayType === 'PSC') {
                    // In-house agent: FinalPrice × rate%
                    if (isInHousePsc(line._origRate, line._origAmount)) {
                        line.commPay = parseFloat((finalPrice * rate / 100).toFixed(2));
                    } else if (isCoBroke) {
                        // Co-broke PSC: (CommBase/2) × rate% - (AgentDisc/2)
                        line.commPay = parseFloat(Math.max(commBaseHalf * rate / 100 - agentDisc / 2, 0).toFixed(2));
                    } else {
                        // Normal PSC: CommBase × rate% - AgentDisc
                        line.commPay = parseFloat(Math.max(commBase * rate / 100 - agentDisc, 0).toFixed(2));
                    }
                } else {
                    // DOC, TOC (and DOC/2, TOC/2)
                    const base = line.commPayType.includes('/2') ? commBaseHalf : (isCoBroke ? commBaseHalf : commBase);
                    line.commPay = parseFloat((base * rate / 100).toFixed(2));
                }
            } else if (field === 'commPay') {
                line.editMode = 'amount';
                const amount = parseFloat(value) || 0;
                line.commPay = amount;

                // Reverse-calculate rate from amount
                if (line.commPayType === 'PSC') {
                    if (isInHousePsc(line._origRate, line._origAmount)) {
                        // amount = finalPrice × rate / 100 → rate = amount × 100 / finalPrice
                        line.rate = finalPrice ? parseFloat((amount * 100 / finalPrice).toFixed(4)) : 0;
                    } else if (isCoBroke) {
                        // amount = commBaseHalf × rate / 100 - agentDisc/2 → rate = (amount + agentDisc/2) × 100 / commBaseHalf
                        line.rate = commBaseHalf ? parseFloat(((amount + agentDisc / 2) * 100 / commBaseHalf).toFixed(4)) : 0;
                    } else {
                        // amount = commBase × rate / 100 - agentDisc → rate = (amount + agentDisc) × 100 / commBase
                        line.rate = commBase ? parseFloat(((amount + agentDisc) * 100 / commBase).toFixed(4)) : 0;
                    }
                } else {
                    const base = line.commPayType.includes('/2') ? commBaseHalf : (isCoBroke ? commBaseHalf : commBase);
                    line.rate = base ? parseFloat((amount * 100 / base).toFixed(4)) : 0;
                }
            } else if (field === 'paymentTo') {
                line.paymentTo = value;
                const agent = agents.find(a => a.userId === value);
                line.paymentToName = agent
                    ? handleAgentName(agent.firstName, agent.lastName, agent.alias) || ''
                    : '';
            } else {
                line[field] = value;
            }

            updated[index] = line;
            return updated;
        });
    };

    const addRow = () => {
        setLines(prev => [...prev, { paymentTo: '', paymentToName: '', commPayType: 'PSC', commPay: 0, rate: 0, editMode: 'rate' }]);
    };

    const removeRow = (index) => {
        setLines(prev => prev.filter((_, i) => i !== index));
    };

    const totalAmount = lines.reduce((sum, l) => sum + (parseFloat(l.commPay) || 0), 0);

    const handleSubmit = async () => {
        if (!reason.trim()) { alert('Reason is required'); return; }
        setSubmitting(true);
        try {
            const proposedData = JSON.stringify(lines);
            await createAmendment({ salesId, proposedData, reason });
            alert('Amendment submitted for approval');
            onClose();
            onSaved();
        } catch (e) {
            alert('Failed: ' + (typeof e.response?.data === 'string' ? e.response.data : e.message));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
            <DialogTitle sx={{ fontFamily: poppins, fontWeight: 700, fontSize: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                <span>Amend Commission — Sale #{salesId}</span>
                <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {totalAmount > (finalPrice || 0) && (
                    <Alert severity="warning" sx={{ mb: 2, fontFamily: poppins, fontSize: '0.8rem' }}>
                        Total commission ({totalAmount.toFixed(2)}) exceeds final price ({finalPrice?.toFixed(2)})
                    </Alert>
                )}

                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontFamily: poppins, fontWeight: 600, fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase' }}>Type</TableCell>
                                <TableCell sx={{ fontFamily: poppins, fontWeight: 600, fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase' }}>Agent</TableCell>
                                <TableCell sx={{ fontFamily: poppins, fontWeight: 600, fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase' }}>Rate (%)</TableCell>
                                <TableCell sx={{ fontFamily: poppins, fontWeight: 600, fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase' }}>Amount (RM)</TableCell>
                                <TableCell sx={{ width: 40 }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lines.map((line, i) => (
                                <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#F8FAFC' } }}>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <FormControl size="small" sx={{ minWidth: 80 }}>
                                            <Select
                                                value={line.commPayType}
                                                onChange={e => updateLine(i, 'commPayType', e.target.value)}
                                                sx={{ fontFamily: poppins, fontSize: '0.8rem' }}
                                            >
                                                {COMM_TYPES.map(t => <MenuItem key={t} value={t} sx={{ fontFamily: poppins, fontSize: '0.8rem' }}>{t}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <FormControl size="small" sx={{ minWidth: 160 }}>
                                            <Select
                                                value={line.paymentTo || ''}
                                                onChange={e => updateLine(i, 'paymentTo', e.target.value)}
                                                displayEmpty
                                                sx={{ fontFamily: poppins, fontSize: '0.8rem' }}
                                            >
                                                <MenuItem value="" sx={{ fontFamily: poppins, fontSize: '0.8rem' }}><em>Select Agent</em></MenuItem>
                                                {agents.map(a => (
                                                    <MenuItem key={a.userId} value={a.userId} sx={{ fontFamily: poppins, fontSize: '0.8rem' }}>
                                                        {handleAgentName(a.firstName, a.lastName, a.alias) || a.userId}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={line.rate}
                                            onChange={e => updateLine(i, 'rate', e.target.value)}
                                            onFocus={() => { if (line.editMode !== 'rate') updateLine(i, 'editMode', 'rate'); }}
                                            inputProps={{ step: 0.01, min: 0, style: { fontFamily: poppins, fontSize: '0.8rem' } }}
                                            sx={{
                                                width: 90,
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: line.editMode === 'rate' ? '#EFF6FF' : '#F8FAFC',
                                                    border: line.editMode === 'rate' ? '1.5px solid #1A8CFF' : '1px solid #E2E8F0',
                                                    '& fieldset': { border: 'none' },
                                                },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={line.commPay}
                                            onChange={e => updateLine(i, 'commPay', e.target.value)}
                                            onFocus={() => { if (line.editMode !== 'amount') updateLine(i, 'editMode', 'amount'); }}
                                            inputProps={{ step: 0.01, min: 0, style: { fontFamily: poppins, fontSize: '0.8rem' } }}
                                            sx={{
                                                width: 110,
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: line.editMode === 'amount' ? '#EFF6FF' : '#F8FAFC',
                                                    border: line.editMode === 'amount' ? '1.5px solid #1A8CFF' : '1px solid #E2E8F0',
                                                    '& fieldset': { border: 'none' },
                                                },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <IconButton size="small" onClick={() => removeRow(i)} sx={{ color: '#EF4444' }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, mb: 2 }}>
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addRow}
                        sx={{ textTransform: 'none', fontFamily: poppins, fontSize: '0.8rem', color: '#1A8CFF' }}
                    >
                        Add Row
                    </Button>
                    <Typography sx={{ fontFamily: poppins, fontSize: '0.85rem', fontWeight: 600, color: '#0F172A' }}>
                        Total: RM {totalAmount.toFixed(2)}
                    </Typography>
                </Box>

                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    label="Reason for Amendment"
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="Explain why the commission needs to be amended..."
                    required
                    sx={{ mt: 1 }}
                    InputProps={{ sx: { fontFamily: poppins, fontSize: '0.85rem' } }}
                    InputLabelProps={{ sx: { fontFamily: poppins, fontSize: '0.85rem' } }}
                />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} sx={{ fontFamily: poppins, textTransform: 'none' }}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting}
                    sx={{
                        fontFamily: poppins, fontWeight: 600, textTransform: 'none', borderRadius: '8px',
                        background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                    }}
                >
                    {submitting ? 'Submitting...' : 'Submit for Approval'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
